import { Box, Image, rem } from '@mantine/core'
import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react'

import type {
  BoggleRunSummaryRatingConfig,
  BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import { BOGGLE_RATING_BADGE_IMAGES } from '~/boggle/ui/ratings/ratingAssets'

import classes from '~/boggle/ui/run/RunResultsRatingBadge.module.css'

const runResultsRatingBadgePreloadUrls = new Set<string>()

/**
 * Warms the HTTP cache (and decode pipeline) for all rating badge PNGs so the results
 * reveal animation isn’t thrown off by a first-time image fetch. Safe to call repeatedly.
 *
 * Vite only emits stable URLs in the bundle — it does **not** preload image bytes in production.
 */
export function preloadRunResultsRatingBadgeImages() {
  for (const url of Object.values(BOGGLE_RATING_BADGE_IMAGES)) {
    if (!url || runResultsRatingBadgePreloadUrls.has(url)) {
      continue
    }
    runResultsRatingBadgePreloadUrls.add(url)
    const img = document.createElement('img')
    img.src = url
  }
}

/** Pulse visuals — higher tiers get larger, longer, brighter bursts. */
const RATING_PULSE: Record<
  Exclude<BoggleRunSummaryRatingId, 'none'>,
  {
    color: string
    delayMs?: number
    durationMs: number
    endScale: number
    peakOpacity: number
    sizePx: number
  }
> = {
  bronze: {
    color: 'rgba(205, 140, 75, 0.95)',
    delayMs: 55,
    durationMs: 520,
    endScale: 2.15,
    peakOpacity: 0.38,
    sizePx: 168,
  },
  silver: {
    color: 'rgba(210, 218, 238, 0.94)',
    durationMs: 600,
    endScale: 2.48,
    peakOpacity: 0.48,
    sizePx: 188,
  },
  gold: {
    color: 'rgba(255, 218, 130, 0.96)',
    durationMs: 700,
    endScale: 2.85,
    peakOpacity: 0.58,
    sizePx: 210,
  },
  diamond: {
    color: 'rgba(220, 244, 255, 0.98)',
    durationMs: 760,
    endScale: 3.2,
    peakOpacity: 0.72,
    sizePx: 236,
  },
  'ace': {
    color: 'rgba(205, 165, 255, 0.96)',
    durationMs: 880,
    endScale: 3.95,
    peakOpacity: 0.9,
    sizePx: 268,
  },
}

/** Height of the rating badge — sized to occupy the corner the gold tracker vacates. */
export const RUN_RESULTS_RATING_BADGE_HEIGHT = 100

/** Rating badge PNGs are 183×165; reserve width so pulse centers before decode. */
const RUN_RESULTS_RATING_BADGE_ASPECT = 183 / 165

/**
 * Compact rating badge that pops in (scale) after the tier sequence completes, with a
 * one-shot radial pulse keyed to the rating color. Renders nothing for `rating.id === 'none'`.
 */
export function RunResultsRatingBadge({
  rating,
  showRating,
}: {
  rating: BoggleRunSummaryRatingConfig
  showRating: boolean
}) {
  const url = BOGGLE_RATING_BADGE_IMAGES[rating.id]
  const [revealKey, setRevealKey] = useState(0)
  const prevShowRatingRef = useRef(false)

  useLayoutEffect(() => {
    const wasShowing = prevShowRatingRef.current
    prevShowRatingRef.current = showRating
    if (showRating && !wasShowing) {
      setRevealKey((key) => key + 1)
    }
  }, [showRating])

  if (!url) {
    return null
  }

  const pulse =
    rating.id !== 'none' && rating.id in RATING_PULSE
      ? RATING_PULSE[rating.id as Exclude<BoggleRunSummaryRatingId, 'none'>]
      : null

  return (
    <Box
      className={`${classes.root} ${showRating ? '' : classes.rootHidden}`}
      h={RUN_RESULTS_RATING_BADGE_HEIGHT}
    >
      {showRating ? (
        <Box
          key={revealKey}
          className={classes.reveal}
          h={RUN_RESULTS_RATING_BADGE_HEIGHT}
          w={RUN_RESULTS_RATING_BADGE_HEIGHT * RUN_RESULTS_RATING_BADGE_ASPECT}
        >
          {pulse ? (
            <div
              aria-hidden
              className={classes.pulse}
              style={
                {
                  '--rating-pulse-color': pulse.color,
                  '--rating-pulse-delay': `${pulse.delayMs ?? 0}ms`,
                  '--rating-pulse-duration': `${pulse.durationMs}ms`,
                  '--rating-pulse-end-scale': String(pulse.endScale),
                  '--rating-pulse-peak-opacity': String(pulse.peakOpacity),
                  '--rating-pulse-size': `${pulse.sizePx}px`,
                } as CSSProperties
              }
            />
          ) : null}
          <Box className={classes.badgePop} h={RUN_RESULTS_RATING_BADGE_HEIGHT}>
            <Image
              alt={rating.label}
              fit="contain"
              h={RUN_RESULTS_RATING_BADGE_HEIGHT}
              maw="100%"
              src={url}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}
